import { Grid } from "@mui/material";
import { observer } from "mobx-react";
import React from "react";
import theme from "Theme/AppTheme";
import {
    SelectedDataPoint,
    SelectedDriver,
    SelectedTopic,
} from "./DrilldownTypes";
import { getXDateRangeLabel } from "../ChartCard/dateBucketLabels";
import { SignalsTemporalLabels } from "components/Signals/SignalsModels";
import { Moment } from "moment";

type Props = {
    isTemporal: boolean;
    selectedPayload: SelectedDataPoint | SelectedTopic | SelectedDriver;
    isPercentage: boolean;
    temporalData?: {
        temporalGrain: SignalsTemporalLabels;
        startDate: Moment | undefined;
        endDate: Moment | undefined;
    };
    preferSeries: boolean;
    /**
     * Drops the big value, leaving the label alone. Insight (small) cards set this: their selection
     * is the whole card, and the stat is already the only thing the card renders, so repeating it
     * in the menu says nothing the user cannot see behind it.
     */
    hideValue?: boolean;
    pointTooltip?: React.ReactElement;
};

const PointMenuItemHeader: React.FC<Props> = observer(
    ({
        isTemporal,
        selectedPayload,
        isPercentage,
        temporalData,
        preferSeries,
        hideValue,
        pointTooltip,
    }) => {
        if (pointTooltip && !hideValue) {
            return (
                <Grid
                    sx={{
                        color: "black",
                        maxWidth: "250px",
                        whiteSpace: "normal",
                    }}
                >
                    {pointTooltip}
                </Grid>
            );
        }

        const selectedEntityLabel = selectedPayload.hasOwnProperty(
            "selectedTopic",
        )
            ? (selectedPayload as SelectedTopic).selectedTopic
            : selectedPayload.hasOwnProperty("selectedDriver")
              ? (selectedPayload as SelectedDriver).selectedDriver
              : undefined;
        let formattedTitle =
            selectedEntityLabel ??
            (!!(selectedPayload as SelectedDataPoint).series && preferSeries
                ? (selectedPayload as SelectedDataPoint).series
                : (selectedPayload as SelectedDataPoint).x);

        let formattedSubtitle =
            !!(selectedPayload as SelectedDataPoint).series && preferSeries
                ? (selectedPayload as SelectedDataPoint).x
                : null;

        if (isTemporal && temporalData) {
            const dateRangeLabel = getXDateRangeLabel(
                (selectedPayload as SelectedDataPoint).x,
                temporalData.temporalGrain,
                temporalData.startDate,
                temporalData.endDate,
            );

            if ((selectedPayload as SelectedDataPoint).series && preferSeries) {
                formattedSubtitle = dateRangeLabel;
            } else {
                formattedSubtitle = "";
                formattedTitle = dateRangeLabel;
            }
        }

        let formattedYValue =
            Number(
                Number((selectedPayload as SelectedDataPoint).y)?.toFixed(
                    isPercentage ? 1 : 0,
                ),
            ).toLocaleString() + (isPercentage ? "%" : "");

        if (selectedEntityLabel || hideValue) {
            formattedYValue = "";
        }

        const menuHeaderComponent = (
            <>
                {!!formattedTitle && (
                    <Grid
                        item
                        xs={12}
                        fontWeight={600}
                        style={{ textWrap: "wrap" }}
                    >
                        {formattedTitle}
                    </Grid>
                )}
                {!!formattedYValue && (
                    <Grid
                        item
                        xs={12}
                        style={{
                            fontSize: "34px",
                            color: theme.palette.blue.main,
                            textWrap: "wrap",
                        }}
                        fontWeight={400}
                        data-testId="datapoint-y-value"
                    >
                        {formattedYValue}
                    </Grid>
                )}
                {!!formattedSubtitle && (
                    <Grid
                        item
                        xs={12}
                        fontWeight={!formattedTitle ? 600 : "normal"}
                        style={{
                            textWrap: "wrap",
                        }}
                    >
                        {formattedSubtitle}
                    </Grid>
                )}
            </>
        );

        return (
            <Grid
                item
                xs={12}
                container
                sx={{
                    color: "black",
                    maxWidth: "250px",
                }}
                gap={1}
            >
                {menuHeaderComponent}
            </Grid>
        );
    },
);

export default PointMenuItemHeader;
